import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { apiErrorMessage } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Notice } from '@/components/ui/notice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAccounts, useActions } from '@/hooks/use-dba-queries';
import { formatAud, formatDateTime } from '@/lib/format';

const RECENT_ACTIONS_LIMIT = 20;

export function AccountsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  const accounts = useAccounts(q);
  const actions = useActions(RECENT_ACTIONS_LIMIT);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = draft.trim();
      if (next !== q) {
        setSearchParams(next ? { q: next } : {}, { replace: true });
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [draft, q, setSearchParams]);

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-medium">Accounts</h1>
        <Input
          className="max-w-xs"
          placeholder="Search email, account or name"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-label="Search accounts"
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          {accounts.isError && <Notice tone="destructive">{apiErrorMessage(accounts.error)}</Notice>}
          {accounts.isPending && <p className="text-sm text-muted-foreground">Loading accounts…</p>}
          {accounts.data && accounts.data.length === 0 && (
            <p className="text-sm text-muted-foreground">No accounts match this search.</p>
          )}
          {accounts.data && accounts.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Hubs</TableHead>
                  <TableHead className="text-right">Members</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Discount</TableHead>
                  <TableHead>Operator</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.data.map((account) => (
                  <TableRow
                    key={account.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/accounts/${account.id}`)}
                  >
                    <TableCell>
                      <div className="font-medium">{account.accountName}</div>
                      <div className="text-xs text-muted-foreground">
                        {account.firstName} {account.surname}
                      </div>
                    </TableCell>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>
                      <Badge variant={account.accountStatus === 'VERIFIED' ? 'success' : 'warning'}>
                        {account.accountStatus}
                      </Badge>
                      {account.isDeleted && (
                        <Badge variant="destructive" className="ml-1">
                          Deleted
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{account.hubCount}</TableCell>
                    <TableCell className="text-right">{account.memberCount}</TableCell>
                    <TableCell>{account.subscriptionStatus ?? 'None'}</TableCell>
                    <TableCell className="text-right">{formatAud(account.balanceAud)}</TableCell>
                    <TableCell className="text-right">
                      {account.discountPercent > 0 ? `${account.discountPercent}%` : ''}
                    </TableCell>
                    <TableCell>{account.isOperator && <Badge variant="info">Operator</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent operator actions</CardTitle>
        </CardHeader>
        <CardContent>
          {actions.isError && <Notice tone="destructive">{apiErrorMessage(actions.error)}</Notice>}
          {actions.data && actions.data.length === 0 && (
            <p className="text-sm text-muted-foreground">No operator actions yet.</p>
          )}
          {actions.data && actions.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {actions.data.map((action) => (
                  <TableRow key={action.id}>
                    <TableCell className="whitespace-nowrap">{formatDateTime(action.createdAt)}</TableCell>
                    <TableCell>{action.operatorEmail}</TableCell>
                    <TableCell className="whitespace-nowrap">{action.action}</TableCell>
                    <TableCell>{action.summary}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
